// src/dashboard/onboarding/list.tsx
import { Button, theme } from "@lb-lib/ui";
import { renderWithLoader } from "@loopbound-ares/components";
import { useDismissOnboardingTask, useOnboardingGroup, useSkipOnboardingGroup } from "@loopbound-ares/state";
import { RiCheckLine } from "@remixicon/react";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { jsx, jsxs } from "react/jsx-runtime";
var Wrapper = styled.section.withConfig({
  componentId: "sc-sc2nrs-0"
})`display:flex;flex-direction:column;gap:6px;position:relative;`;
var Header = styled.header.withConfig({
  componentId: "sc-sc2nrs-1"
})`display:flex;justify-content:space-between;align-items:center;gap:10px;& > span{font-size:14px;font-weight:600;}`;
var Content = styled.ul.withConfig({
  componentId: "sc-sc2nrs-2"
})`display:flex;flex-direction:column;gap:6px;list-style:none;padding:0;margin:0;`;
var Task = styled.li.withConfig({
  componentId: "sc-sc2nrs-3"
})`display:flex;gap:10px;align-items:center;position:relative;cursor:default;p{font-size:14px;font-weight:500;flex-grow:1;flex-shrink:1;}button{opacity:0;}&:hover,&:focus-within{button{opacity:1;}}`;
var Icon = styled.figure.withConfig({
  componentId: "sc-sc2nrs-4"
})`display:flex;align-items:center;justify-content:center;height:24px;width:24px;border-radius:50%;transition:all 0.2s;svg{height:14px;width:auto;}`;
var Progress = styled.div.withConfig({
  componentId: "sc-sc2nrs-5"
})`position:relative;background:${theme.colors.gray300};z-index:1;height:20px;display:flex;align-items:center;border-radius:10px;overflow:hidden;& > div{background:${theme.colors.gray400};z-index:2;position:absolute;top:0;left:0;bottom:0;transition:all 0.5s;}& > span{position:relative;z-index:2;padding:0px 6px;font-size:10px;font-weight:600;}`;
var SkipOnboarding = styled.button.withConfig({
  componentId: "sc-sc2nrs-6"
})`position:absolute;right:10px;bottom:-20px;height:20px;border:${theme.colors.gray300} solid 1px;border-radius:50px;box-shadow:${theme.shadows.small};padding:0 8px;display:flex;align-items:center;justify-content:center;background:${theme.colors.background};color:${theme.colors.gray700};transition:all 0.2s;font-size:12px;font-weight:600;&:hover,&:focus{transform:scale(1.05);}&:active{transform:scale(0.95);}&:disabled{cursor:not-allowed;opacity:0.5;}`;
var useOnboardingList = (opts) => {
  let tasks = useMemo(() => opts?.tasks.map((t) => ({
    key: t.key,
    status: t.status
  })) ?? [], [opts?.tasks]);
  let group = useOnboardingGroup(opts ? {
    onboardingGroupKey: opts.onboardingGroupKey,
    tasks
  } : null);
  return {
    ...group,
    shouldRender: !!(group.data && !group.data?.isCompleted || !opts?.hideIfCompleted)
  };
};
var OnboardingList = (props) => {
  let group = useOnboardingList(props);
  let {
    onboardingGroupKey,
    hideIfCompleted,
    tasks,
    title
  } = props;
  let dismissOnboardingTask = useDismissOnboardingTask();
  let skipOnboardingGroup = useSkipOnboardingGroup();
  let navigate = useNavigate();
  if (hideIfCompleted && (!group.data || group.data.isCompleted || group.data.isSkipped))
    return null;
  return /* @__PURE__ */ jsxs(Wrapper, { children: [
    /* @__PURE__ */ jsxs(Header, { children: [
      /* @__PURE__ */ jsx("span", { children: title }),
      props.skipOnboarding == "enabled" && /* @__PURE__ */ jsx(Button, { loading: skipOnboardingGroup.isLoading, title: "Skip", size: "1", variant: "soft", onClick: () => {
        skipOnboardingGroup.mutate({
          onboardingGroupKey,
          instanceId: group.data.instanceId
        });
      }, children: "Skip" }),
      group.data && /* @__PURE__ */ jsxs(Progress, { children: [
        /* @__PURE__ */ jsx("div", { style: {
          width: `${group.data?.completedPercent}%`
        } }),
        /* @__PURE__ */ jsxs("span", { children: [
          group.data?.completedPercent,
          "%"
        ] })
      ] })
    ] }),
    renderWithLoader({
      group
    })(() => /* @__PURE__ */ jsx(Content, { children: tasks.map((task) => {
      let groupTask = group.data.tasks.find((t) => t.key == task.key);
      let openLink = () => {
        if (task.link)
          navigate(task.link);
      };
      return /* @__PURE__ */ jsxs(Task, { ...task.link && groupTask?.status == "pending" ? {
        onClick: openLink,
        onKeyPress: openLink,
        role: "link",
        tabIndex: 0,
        style: {
          cursor: "pointer"
        }
      } : {}, children: [
        /* @__PURE__ */ jsx(Icon, { style: {
          background: groupTask?.status != "pending" ? theme.colors.blue600 : theme.colors.gray300
        }, children: groupTask?.status != "pending" ? /* @__PURE__ */ jsx(RiCheckLine, {}) : task.icon }),
        /* @__PURE__ */ jsx("p", { style: {
          textDecoration: groupTask?.status != "pending" ? "line-through" : "none"
        }, children: task.name }),
        /* @__PURE__ */ jsx("div", { style: {
          position: "absolute",
          right: 0,
          top: 0,
          bottom: 0,
          transition: "all 0.2s",
          pointerEvents: groupTask?.status == "pending" ? "all" : "none",
          opacity: groupTask?.status == "pending" ? 1 : 0
        }, children: /* @__PURE__ */ jsx(Button, { loading: dismissOnboardingTask.isLoading && dismissOnboardingTask.input?.onboardingTaskKey == task.key, disabled: groupTask?.status != "pending", title: "Dismiss", size: "1", variant: "soft", onClick: (e) => {
          e.stopPropagation();
          dismissOnboardingTask.mutate({
            onboardingTaskKey: task.key,
            onboardingGroupKey,
            instanceId: group.data.instanceId
          });
        }, iconLeft: /* @__PURE__ */ jsx(RiCheckLine, {}) }) })
      ] }, task.key);
    }) })),
    props.skipOnboarding == "enabled_floating_button" && /* @__PURE__ */ jsx(SkipOnboarding, { onClick: () => {
      skipOnboardingGroup.mutate({
        onboardingGroupKey,
        instanceId: group.data.instanceId
      });
    }, disabled: skipOnboardingGroup.isLoading, children: "Skip" })
  ] });
};

export {
  useOnboardingList,
  OnboardingList
};
//# sourceMappingURL=chunk-5KBKVBSR.js.map